<script setup lang="ts">
import { ref, onUnmounted } from 'vue';
import { useMapStore } from '@/stores/map';
import { useEditorStore, EditorTool } from '@/stores/editor';
import Toolbar from './Toolbar.vue';
import MapViewport from './MapViewport.vue';
import TilePalette from './TilePalette.vue';
import SpritesheetViewer from './SpritesheetViewer.vue';
import ObjectSelector from './ObjectSelector.vue';
import PropertiesPanel from './PropertiesPanel.vue';
import Minimap from './Minimap.vue';
import StatusBar from './StatusBar.vue';
import NewMapDialog from './NewMapDialog.vue';

const mapStore = useMapStore();
const editorStore = useEditorStore();

const showNewMapDialog = ref(false);
const isDraggingFile = ref(false);
const minimapFloating = ref(false);

// Right sidebar resize state
const rightSidebarWidth = ref(320);
const isResizingRightSidebar = ref(false);
const minRightSidebarWidth = 200;
const maxRightSidebarWidth = 800;

function startRightSidebarResize(e: MouseEvent): void {
    e.preventDefault();
    isResizingRightSidebar.value = true;
    document.addEventListener('mousemove', handleRightSidebarResize);
    document.addEventListener('mouseup', stopRightSidebarResize);
}

function handleRightSidebarResize(e: MouseEvent): void {
    if (!isResizingRightSidebar.value) return;

    const newWidth = window.innerWidth - e.clientX;
    rightSidebarWidth.value = Math.max(minRightSidebarWidth, Math.min(maxRightSidebarWidth, newWidth));
}

function stopRightSidebarResize(): void {
    isResizingRightSidebar.value = false;
    document.removeEventListener('mousemove', handleRightSidebarResize);
    document.removeEventListener('mouseup', stopRightSidebarResize);
}

onUnmounted(() => {
    document.removeEventListener('mousemove', handleRightSidebarResize);
    document.removeEventListener('mouseup', stopRightSidebarResize);
});

// Drag and drop
function handleDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types.includes('Files')) {
        isDraggingFile.value = true;
        e.dataTransfer.dropEffect = 'copy';
    }
}

function handleDragLeave(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    isDraggingFile.value = false;
}

async function handleDrop(e: DragEvent): Promise<void> {
    e.preventDefault();
    e.stopPropagation();
    isDraggingFile.value = false;

    const file = e.dataTransfer?.files[0];
    if (file && file.name.toLowerCase().endsWith('.amd')) {
        await mapStore.loadMap(file);
    }
}

// File operations
async function handleOpen(): Promise<void> {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.amd';
    input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
            await mapStore.loadMap(file);
        }
    };
    input.click();
}

function handleSave(): void {
    mapStore.saveMap();
}

function handleNew(): void {
    showNewMapDialog.value = true;
}

function handleCreateMap(width: number, height: number, terrain: number): void {
    mapStore.newMap(width, height, terrain);
    showNewMapDialog.value = false;
}

// Keyboard shortcuts
function handleKeydown(e: KeyboardEvent): void {
    // Ctrl/Cmd shortcuts
    if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
            case 'n':
                e.preventDefault();
                handleNew();
                break;
            case 'o':
                e.preventDefault();
                handleOpen();
                break;
            case 's':
                e.preventDefault();
                handleSave();
                break;
            case 'z':
                e.preventDefault();
                if (e.shiftKey) {
                    mapStore.redo();
                } else {
                    mapStore.undo();
                }
                break;
            case 'y':
                e.preventDefault();
                mapStore.redo();
                break;
        }
    }

    // Brush size shortcuts
    if (e.key === '[') {
        editorStore.setBrushSize(editorStore.brushSize - 1);
    } else if (e.key === ']') {
        editorStore.setBrushSize(editorStore.brushSize + 1);
    }

    // Don't handle shortcuts if typing in an input
    if ((e.target as HTMLElement).tagName === 'INPUT') return;

    // View shortcuts
    if (e.key === 'g' && !e.ctrlKey) {
        editorStore.showGrid = !editorStore.showGrid;
    }

    // Tool shortcuts (single keys, no modifiers)
    if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        switch (e.key.toLowerCase()) {
            case 'v':
                editorStore.setTool(EditorTool.SELECT);
                break;
            case 'b':
                editorStore.setTool(EditorTool.PAINT_TILE);
                break;
            case 'o':
                editorStore.setTool(EditorTool.PAINT_OBJECT);
                break;
            case 'e':
                editorStore.setTool(EditorTool.ERASE_OBJECT);
                break;
            case 'i':
                editorStore.setTool(EditorTool.EYEDROPPER);
                break;
            case 'x':
                editorStore.setTool(EditorTool.TOGGLE_BLOCKED);
                break;
            case 't':
                editorStore.setTool(EditorTool.TOGGLE_TELEPORT);
                break;
        }
    }
}
</script>

<template>
    <div
        class="editor-layout"
        @keydown="handleKeydown"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
        tabindex="0"
    >
        <!-- Drop zone overlay -->
        <div class="drop-zone-overlay" v-if="isDraggingFile">
            <div class="drop-zone-content">
                <div class="drop-icon">📂</div>
                <p>Drop .amd file to open</p>
            </div>
        </div>

        <!-- Menu bar -->
        <header class="menu-bar">
            <div class="menu-left">
                <a href="http://helbreathx.net" target="_blank" class="logo-link" title="Visit HelbreathX">
                    <img src="/favicon.png" alt="HelbreathX" class="logo-img" />
                </a>
                <a href="http://discord.gg/helbreath" target="_blank" class="discord-link" title="Join our Discord">
                    Join our Discord
                </a>
                <div class="menu-items">
                    <button class="menu-btn" @click="handleNew">New</button>
                    <button class="menu-btn" @click="handleOpen">Open</button>
                    <button class="menu-btn" @click="handleSave" :disabled="!mapStore.hasMap">Save</button>
                </div>
            </div>
            <div class="menu-right">
                <span class="filename" v-if="mapStore.hasMap">
                    {{ mapStore.filename }}
                    <span class="dirty-indicator" v-if="mapStore.isDirty">*</span>
                </span>
            </div>
        </header>

        <!-- Toolbar -->
        <Toolbar />

        <!-- Main content area -->
        <div class="content-area">
            <!-- Left sidebar -->
            <aside class="sidebar left-sidebar" v-if="editorStore.showTilePalette">
                <TilePalette />
            </aside>

            <!-- Map viewport (center) -->
            <main class="viewport-container">
                <MapViewport />
                <!-- Floating properties panel -->
                <PropertiesPanel v-if="editorStore.showProperties" class="floating-properties" />
                <!-- Floating minimap (when popped out) -->
                <Minimap v-if="editorStore.showMinimap && minimapFloating" class="floating-minimap" :floating="true" @toggle-float="minimapFloating = false" />
            </main>

            <!-- Right sidebar resize handle -->
            <div
                class="sidebar-resize-handle"
                :class="{ resizing: isResizingRightSidebar }"
                @mousedown="startRightSidebarResize"
            ></div>

            <!-- Right sidebar -->
            <aside
                class="sidebar right-sidebar"
                :class="{ resizing: isResizingRightSidebar }"
                :style="{ width: rightSidebarWidth + 'px' }"
            >
                <!-- Show SpritesheetViewer for tiles, ObjectSelector for objects -->
                <SpritesheetViewer v-if="editorStore.paletteTab === 'tiles'" />
                <ObjectSelector v-else />
                <!-- Docked minimap (when not floating) -->
                <Minimap v-if="editorStore.showMinimap && !minimapFloating" :floating="false" @toggle-float="minimapFloating = true" />
            </aside>
        </div>

        <!-- Status bar -->
        <StatusBar />

        <!-- Dialogs -->
        <NewMapDialog
            v-if="showNewMapDialog"
            @create="handleCreateMap"
            @cancel="showNewMapDialog = false"
        />
    </div>
</template>

<style scoped>
.editor-layout {
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    outline: none;
    position: relative;
}

.drop-zone-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(99, 102, 241, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    pointer-events: none;
}

.drop-zone-content {
    text-align: center;
    color: white;
}

.drop-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.drop-zone-content p {
    font-size: 24px;
    font-weight: 600;
}

.menu-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 36px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    padding: 0 12px;
}

.menu-left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
}

.logo-img {
    width: 24px;
    height: 24px;
    image-rendering: pixelated;
}

.discord-link {
    font-size: 12px;
    color: #8b9eff;
    text-decoration: none;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.15s ease;
}

.discord-link:hover {
    background: rgba(139, 158, 255, 0.15);
    color: #b4c2ff;
}

.menu-items {
    display: flex;
    gap: 4px;
}

.menu-btn {
    background: transparent;
    border: none;
    color: var(--text-secondary);
    padding: 6px 12px;
    font-size: 13px;
    border-radius: 4px;
    transition: all 0.15s ease;
}

.menu-btn:hover:not(:disabled) {
    background: var(--bg-tertiary);
    color: var(--text-primary);
}

.menu-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.menu-right {
    display: flex;
    align-items: center;
}

.filename {
    font-size: 13px;
    color: var(--text-secondary);
}

.dirty-indicator {
    color: var(--warning-color);
    margin-left: 2px;
}

.content-area {
    display: flex;
    flex: 1;
    overflow: hidden;
}

.sidebar {
    background: var(--bg-panel);
    border-color: var(--border-color);
    overflow-y: auto;
}

.left-sidebar {
    border-right: 1px solid var(--border-color);
    flex-shrink: 0;
}

.sidebar-resize-handle {
    width: 6px;
    background: var(--border-color);
    cursor: ew-resize;
    flex-shrink: 0;
    transition: background 0.15s ease;
}

.sidebar-resize-handle:hover,
.sidebar-resize-handle.resizing {
    background: var(--accent-color);
}

.right-sidebar {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.right-sidebar.resizing {
    user-select: none;
}

.viewport-container {
    flex: 1;
    overflow: hidden;
    position: relative;
}

.floating-properties {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 100;
    background: rgba(30, 30, 46, 0.85);
    backdrop-filter: blur(8px);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    max-width: 220px;
    pointer-events: auto;
}

.floating-minimap {
    position: absolute;
    bottom: 12px;
    right: 12px;
    z-index: 100;
    background: rgba(30, 30, 46, 0.85);
    backdrop-filter: blur(8px);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    pointer-events: auto;
}
</style>
